import { useEffect, useRef, useState } from "react";
import { Copy, Pause, Play, Trash2, X } from "lucide-react";
import {
  subscribeLiveRtcLog,
  type RtcLoggedEvent,
} from "@/features/rtc/rtcLogger";

const MAX_ROWS = 300;

/** Types that signal something went wrong — highlighted red in the stream. */
const ERROR_RE = /error|blocked|gave_up|dropped|no_local_media|failed/;

const shortPeer = (peerId?: string) =>
  peerId ? (peerId.startsWith("guest:") ? peerId.slice(0, 14) : peerId.slice(0, 8)) : "—";

const fmtTime = (ts: number) => {
  const d = new Date(ts);
  return (
    d.toLocaleTimeString("pl-PL", { hour12: false }) +
    "." +
    String(d.getMilliseconds()).padStart(3, "0")
  );
};

interface Props {
  onClose: () => void;
}

/**
 * Live RTC log stream for developers, rendered inside the meeting room. Taps
 * the same events the rtcLogger ships to `/realtime/diagnostics/` (plus the
 * live-only 10 s stats samples), so what a dev sees here is exactly what the
 * backend RtcDiagnosticEvent table will show for the session.
 */
export function MeetingDebugPanel({ onClose }: Props) {
  const [events, setEvents] = useState<RtcLoggedEvent[]>([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("");
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () =>
      subscribeLiveRtcLog((event) => {
        if (pausedRef.current) return;
        setEvents((prev) => {
          const next = prev.length >= MAX_ROWS ? prev.slice(-MAX_ROWS + 1) : [...prev];
          next.push(event);
          return next;
        });
      }),
    [],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (el && !paused) el.scrollTop = el.scrollHeight;
  }, [events, paused]);

  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? events.filter((e) =>
        `${e.event_type} ${e.peer_id ?? ""} ${JSON.stringify(e.payload ?? {})}`
          .toLowerCase()
          .includes(needle),
      )
    : events;

  const copyAll = () => {
    void navigator.clipboard
      ?.writeText(visible.map((e) => JSON.stringify(e)).join("\n"))
      .catch(() => {});
  };

  return (
    <div className="flex h-44 shrink-0 flex-col border-t border-[var(--color-border)] bg-black/90 font-mono text-[11px] text-gray-200">
      <div className="flex items-center gap-2 border-b border-white/10 px-2 py-1">
        <span className="font-semibold text-white">Logi RTC</span>
        <span className="text-gray-400">
          {visible.length}
          {needle ? ` / ${events.length}` : ""}
        </span>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filtruj (typ, peer, payload)…"
          className="min-w-0 flex-1 rounded border border-white/15 bg-transparent px-1.5 py-0.5 text-[11px] text-gray-200 placeholder:text-gray-500 focus:outline-none"
        />
        <button
          type="button"
          title={paused ? "Wznów" : "Pauza"}
          onClick={() => setPaused((p) => !p)}
          className="rounded p-1 text-gray-300 hover:bg-white/10"
        >
          {paused ? <Play size={12} /> : <Pause size={12} />}
        </button>
        <button
          type="button"
          title="Kopiuj widoczne (JSONL)"
          onClick={copyAll}
          className="rounded p-1 text-gray-300 hover:bg-white/10"
        >
          <Copy size={12} />
        </button>
        <button
          type="button"
          title="Wyczyść"
          onClick={() => setEvents([])}
          className="rounded p-1 text-gray-300 hover:bg-white/10"
        >
          <Trash2 size={12} />
        </button>
        <button
          type="button"
          title="Zamknij panel"
          onClick={onClose}
          className="rounded p-1 text-gray-300 hover:bg-white/10"
        >
          <X size={12} />
        </button>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-2 py-1">
        {visible.length === 0 && (
          <p className="py-2 text-gray-500">
            {paused ? "Pauza — strumień zatrzymany." : "Czekam na zdarzenia…"}
          </p>
        )}
        {visible.map((e, i) => {
          const isError = ERROR_RE.test(e.event_type);
          const isStats = e.event_type.startsWith("stats");
          const payload = e.payload ? JSON.stringify(e.payload) : "";
          return (
            <div
              key={`${e.ts}-${i}`}
              className={`flex gap-2 whitespace-nowrap leading-4 ${
                isError ? "text-red-400" : isStats ? "text-gray-500" : ""
              }`}
            >
              <span className="shrink-0 text-gray-500">{fmtTime(e.ts)}</span>
              <span className="w-[7.5rem] shrink-0 truncate text-sky-400" title={e.peer_id}>
                {shortPeer(e.peer_id)}
              </span>
              <span className="shrink-0 font-semibold">{e.event_type}</span>
              <span className="truncate text-gray-400" title={payload}>
                {payload}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
